import { APIContext } from "../../types";
import { makePath } from "../../../../api/url";
import * as APITypes from "../../../../api/cur8/generated-types/v1/Api";
import { getJSON, postJSON } from "../../request";
import { applyPagination } from "../../pagination";

export type BillingApi = ReturnType<typeof createBillingAPIv1>;

export function createBillingAPIv1({
  buildURL,
  fetchJSON,
  fetchPagedJSON,
}: APIContext) {
  function getCart(patientId: string) {
    const path = makePath(["api", "v1", "patient", patientId, "cart"]);
    const url = buildURL(path);

    const request = getJSON(url);
    return fetchJSON<APITypes.Cart>(request);
  }

  function updateCart(
    patientId: string,
    body: APITypes.UpdatePatientCartRequest
  ) {
    const path = makePath(["api", "v1", "patient", patientId, "cart"]);
    const url = buildURL(path);

    const request = postJSON(url, body);
    return fetchJSON<APITypes.Cart>(request);
  }

  function getProduct(productId: string) {
    const path = makePath(["api", "v1", "billing", "product", productId]);
    const url = buildURL(path);

    const request = getJSON(url);
    return fetchJSON<APITypes.Product>(request);
  }

  function getDiscountTokenCode(discountTokenCode: string) {
    const path = makePath([
      "api",
      "v1",
      "billing",
      "discount-token",
      discountTokenCode,
    ]);
    const url = buildURL(path);

    const request = getJSON(url);
    return fetchJSON<APITypes.DiscountToken>(request);
  }

  function getPatientDiscountTokenCodes(patientId: string) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "billing",
      "discount-token",
    ]);
    const url = buildURL(path);

    applyPagination(url.searchParams, {});

    const request = getJSON(url);
    return fetchPagedJSON<APITypes.DiscountToken>(request);
  }

  /**
   * Returns connection-token required for card reader
   * @returns APITypes.StripeConnectionSecret
   */
  function getStripeConnectionToken() {
    const path = makePath([
      "api",
      "v1",
      "billing",
      "stripe",
      "connection-token",
    ]);
    const url = buildURL(path);
    const request = getJSON(url);
    return fetchJSON<APITypes.StripeConnectionSecret>(request);
  }

  /**
   * Get (create) payment intent for a physical card (for the card reader)
   * @param patientId
   */
  function getCardPaymentIntent(patientId: string) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "stripe",
      "create-payment-intent",
    ]);
    const url = buildURL(path);
    const request = postJSON(url, undefined);
    return fetchJSON<APITypes.StripePaymentIntent>(request);
  }

  function hasPaymentMethod({ patientId }: { patientId: string }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "stripe",
      "payment-method",
    ]);
    const url = buildURL(path);
    const request = getJSON(url);

    return fetchJSON<APITypes.StripeCustomerPaymentMethod>(request);
  }

  function getPatientOrders({
    patientId,
    ...options
  }: APITypes.BillingGetPatientOrdersParams) {
    const path = makePath(["api", "v1", "patient", patientId, "order"]);
    const url = buildURL(path);

    const effectiveOptions = {
      ...{ pageSize: 25 },
      ...options,
    };
    applyPagination(url.searchParams, effectiveOptions);

    const request = getJSON(url);
    return fetchPagedJSON<APITypes.Order>(request);
  }

  function getPatientOrder(patientId: string, orderId: string) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "order",
      orderId,
    ]);
    const url = buildURL(path);

    const request = getJSON(url);
    return fetchJSON<APITypes.Order>(request);
  }

  function getProductForPatient(patientId: string, productId: string) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "product",
      productId,
    ]);
    const url = buildURL(path);

    const request = getJSON(url);
    return fetchJSON<APITypes.Product>(request);
  }

  return {
    hasPaymentMethod,
    getCart,
    getCardPaymentIntent,
    getDiscountTokenCode,
    getProduct,
    getPatientDiscountTokenCodes,
    getStripeConnectionToken,
    updateCart,
    getPatientOrders,
    getPatientOrder,
    getProductForPatient,
  };
}
